/**
 * The canvas under an uploaded design system.
 *
 * The uploaded code never runs in the editor's document: the sandbox renders
 * each widget to HTML (sanitized, exactly what publishing gets), and the
 * canvas shows that HTML in a shadow root on the widget's own element, through
 * a React portal. The design system's stylesheet is adopted there as one
 * shared constructable sheet (`:root` read as `:host`), so it styles its own
 * markup and nothing of the editor — and the editor's styles don't leak in.
 * The grid is untouched: from the document, a widget is still one element.
 *
 * Inline editing: the editable text is rendered as marker tokens, which become
 * `<slot>`s after sanitizing, and the editor's own contentEditable spans are
 * projected into them from the light DOM. The text inherits the design
 * system's type (inheritance follows the flattened tree) while React and the
 * caret stay in the editor's document. A design system that transforms the
 * text (so a marker doesn't survive) is shown without inline editing.
 *
 * Rule for design-system authors: styles must not depend on page ancestors
 * (`body .x`, `html[data-…] .x`); the canvas has none, only `:host`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Callout } from "@duro-app/ui";
import type { WidgetProps } from "@website-builder/editor";
import { useUpdateGrid } from "@fredericrous/grid";

import type { DsSandbox } from "../dsSandbox/sandboxHost.js";
import type { DsWidgetKind } from "./dsWidgetElement.js";
import type { DesignSystemState } from "../designSystem/useActiveDesignSystem.js";

interface CanvasCustomDs {
  readonly manifestSha: string;
  readonly name: string;
  readonly css: string;
  readonly sandbox: DsSandbox;
  /** Kinds this release can't render (see designSystemFlows). */
  readonly unsupported: ReadonlySet<string>;
}

const CanvasDsContext = createContext<CanvasCustomDs | null>(null);

/** Around the canvas: which design system its widgets render with. Anything
 *  but a loaded custom one renders in Duro (the header says why). */
export function CanvasDesignSystemProvider({
  state,
  children,
}: {
  readonly state: DesignSystemState;
  readonly children: ReactNode;
}) {
  const value =
    state.kind === "custom"
      ? {
          manifestSha: state.manifestSha,
          name: state.manifest.name,
          css: state.css,
          sandbox: state.sandbox,
          unsupported: state.unsupported,
        }
      : null;
  return (
    <CanvasDsContext.Provider value={value}>
      {children}
    </CanvasDsContext.Provider>
  );
}

// ---------- stylesheet ----------

const sheets = new Map<string, CSSStyleSheet>();

/** One constructable sheet per stylesheet, shared by every widget's root. */
export function hostStyleSheet(css: string): CSSStyleSheet {
  let sheet = sheets.get(css);
  if (!sheet) {
    sheet = new CSSStyleSheet();
    // In a shadow root nothing matches :root; the host is the root there.
    sheet.replaceSync(css.replace(/:root\b/g, ":host"));
    sheets.set(css, sheet);
  }
  return sheet;
}

// ---------- the surface ----------

/**
 * A shadow root on this element, `html` rendered into it (portal), the design
 * system's sheet adopted, and `children` in the light DOM (for slots).
 */
export function ShadowSurface({
  css,
  html,
  children,
}: {
  readonly css: string;
  readonly html: string;
  readonly children?: ReactNode;
}) {
  const [root, setRoot] = useState<ShadowRoot | null>(null);
  const host = useCallback((el: HTMLDivElement | null) => {
    if (el) setRoot(el.shadowRoot ?? el.attachShadow({ mode: "open" }));
  }, []);
  useEffect(() => {
    if (root) root.adoptedStyleSheets = [hostStyleSheet(css)];
  }, [root, css]);
  return (
    // eslint-disable-next-line duro/no-raw-html-element -- a shadow host: attachShadow needs the DOM element itself
    <div ref={host} data-wb-ds-surface="1">
      {children}
      {root &&
        createPortal(
          // eslint-disable-next-line duro/no-raw-html-element -- sanitized design-system markup, set as HTML inside the shadow root
          <div dangerouslySetInnerHTML={{ __html: html }} />,
          root,
        )}
    </div>
  );
}

// ---------- rendering through the sandbox ----------

/** The editable text fields of each kind (rendered as slots). */
const SLOTS: Partial<Record<DsWidgetKind, readonly string[]>> = {
  heading: ["text"],
  card: ["title", "body"],
};
const MULTILINE = new Set(["body"]);

const marker = (field: string) => `wbslot${field}q7x`;

interface Rendered {
  /** With slots for the editable fields, or null when markers didn't survive. */
  readonly slotted: string | null;
  /** With the real text (read-only display). */
  readonly plain: string;
}

const cache = new Map<string, Promise<Rendered>>();
const CACHE_MAX = 500;

function render(
  ds: CanvasCustomDs,
  kind: DsWidgetKind,
  content: Record<string, unknown>,
): Promise<Rendered> {
  const key = `${ds.manifestSha}|${kind}|${JSON.stringify(content)}`;
  let hit = cache.get(key);
  if (!hit) {
    const fields = SLOTS[kind] ?? [];
    const tokenized = { ...content };
    for (const f of fields) tokenized[f] = marker(f);
    hit = ds.sandbox
      .render([
        { key: "plain", contentType: kind, content },
        { key: "slotted", contentType: kind, content: tokenized },
      ])
      .then((r) => {
        let slotted: string | null = r["slotted"] ?? "";
        for (const f of fields) {
          if (!slotted.includes(marker(f))) {
            slotted = null;
            break;
          }
          slotted = slotted.replace(marker(f), `<slot name="${f}"></slot>`);
        }
        return {
          slotted: fields.length ? slotted : null,
          plain: r["plain"] ?? "",
        };
      });
    // A failed render isn't remembered: the next change tries again.
    hit.catch(() => cache.delete(key));
    cache.set(key, hit);
    if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
  }
  return hit;
}

/** Contenteditable text projected into a design system's slot. */
function SlotEditable({
  slot,
  value,
  multiline,
  onCommit,
}: {
  readonly slot: string;
  readonly value: string;
  readonly multiline: boolean;
  readonly onCommit: (text: string) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    // eslint-disable-next-line duro/no-raw-html-element -- react-strict-dom's html.span forwards neither contentEditable nor slot
    <span
      ref={ref}
      slot={slot}
      className="nodrag"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={slot}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          ref.current?.blur();
        }
      }}
      onBlur={() => {
        const t = ref.current?.innerText ?? "";
        if (t !== value) onCommit(t);
      }}
      style={{
        outline: "none",
        cursor: "text",
        userSelect: "text",
        WebkitUserSelect: "text",
        touchAction: "auto",
        whiteSpace: multiline ? "pre-wrap" : "normal",
      }}
    >
      {value}
    </span>
  );
}

function CustomDsWidget({
  ds,
  kind,
  itemId,
  gridId,
  contentProps,
}: WidgetProps & { readonly ds: CanvasCustomDs; readonly kind: DsWidgetKind }) {
  const { preview, ...content } = (contentProps ?? {}) as Record<
    string,
    unknown
  >;
  const { updateItem } = useUpdateGrid(gridId);
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const contentKey = JSON.stringify(content);
  useEffect(() => {
    let live = true;
    render(ds, kind, JSON.parse(contentKey) as Record<string, unknown>).then(
      (r) => live && setRendered(r),
      (e: unknown) => console.warn("[design system] render failed", e),
    );
    return () => {
      live = false;
    };
  }, [ds, kind, contentKey]);

  if (!rendered) return null;
  const editable = !preview && rendered.slotted !== null;
  return (
    <ShadowSurface
      css={ds.css}
      html={editable ? rendered.slotted! : rendered.plain}
    >
      {editable &&
        (SLOTS[kind] ?? []).map((field) => (
          <SlotEditable
            key={field}
            slot={field}
            value={String(content[field] ?? "")}
            multiline={MULTILINE.has(field)}
            onCommit={(text) =>
              updateItem(itemId, {
                contentProps: { ...contentProps, [field]: text },
              })
            }
          />
        ))}
    </ShadowSurface>
  );
}

/** What stands in for a widget the site's design system can't render: it
 *  publishes nothing until the release is replaced (publish refuses it). */
function Unsupported({
  kind,
  dsName,
}: {
  readonly kind: string;
  readonly dsName: string;
}) {
  return (
    <Callout variant="warning">
      {`Not supported by ${dsName}: upload a newer release to use this ${kind}.`}
    </Callout>
  );
}

/** A canvas widget that renders with the site's design system when it has an
 *  uploaded one, and as `Duro` otherwise. */
export function withSiteDesignSystem(
  kind: DsWidgetKind,
  Duro: ComponentType<WidgetProps>,
): ComponentType<WidgetProps> {
  function SiteDsWidget(props: WidgetProps) {
    const ds = useContext(CanvasDsContext);
    if (ds?.unsupported.has(kind))
      return <Unsupported kind={kind} dsName={ds.name} />;
    return ds ? (
      <CustomDsWidget {...props} ds={ds} kind={kind} />
    ) : (
      <Duro {...props} />
    );
  }
  SiteDsWidget.displayName = `SiteDs(${kind})`;
  return SiteDsWidget;
}
